import { useEffect, useState, useCallback, useRef } from "react";
import PageTour from "@/components/PageTour";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Building2, Upload, FileText, Loader2, MapPin, Trash2,
  Plus, Landmark, AlertTriangle, Lock, CheckCircle2, StopCircle, Search, X, ChevronLeft,
} from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { SepaStatusBadge } from "@/components/SepaStatusBadge";
import { CatastoHeaderInfo, useCatastoSync, formatQuota } from "@/components/CatastoPanel";
import { VisuraSoggettoButton } from "@/components/VisuraSoggettoButton";
import { BuildingDocuments } from "@/components/condomini/BuildingDocuments";
import { BuildingNotices } from "@/components/condomini/BuildingNotices";
import { BuildingPolls } from "@/components/condomini/BuildingPolls";
import { PortalForResidentDialog } from "@/components/condomini/PortalForResidentDialog";
import { ResidentDialog, NewBuildingButton, EditBuildingButton } from "@/components/condomini/BuildingForm";
import { PresentationDialog } from "@/components/condomini/PresentationDialog";
import { BuildingsList } from "@/components/condomini/BuildingsList";
import { ImportPreviewDialog } from "@/components/condomini/ImportPreviewDialog";
import { AnagraficaHeader } from "@/components/condomini/AnagraficaHeader";
import { ResidentsTable } from "@/components/condomini/ResidentsTable";
import { CatastoProposalDialog } from "@/components/condomini/CatastoProposalDialog";
import { EMPTY_RESIDENT, SortIcon, fpsKey, roleRank, CONDOMINI_TOUR_STEPS } from "@/components/condomini/condominiHelpers";

export default function Condomini() {
  const [buildings, setBuildings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [residents, setResidents] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [daneaDrag, setDaneaDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletePortalCount, setDeletePortalCount] = useState(0);
  const [sortKey, setSortKey] = useState("unita");
  const [sortDir, setSortDir] = useState("asc");
  const [resSearch, setResSearch] = useState("");
  const [buildingSearch, setBuildingSearch] = useState("");

  // resident CRUD dialog
  const [resDialog, setResDialog] = useState(null); // {mode:'add'|'edit', data}
  const [resSaving, setResSaving] = useState(false);
  const [resToDelete, setResToDelete] = useState(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [addedUnassigned, setAddedUnassigned] = useState({});
  const [assoc, setAssoc] = useState(null);
  const [assocMode, setAssocMode] = useState("add");
  const [assocInterno, setAssocInterno] = useState("");
  const [assocScala, setAssocScala] = useState("");
  const [assocReplaceId, setAssocReplaceId] = useState("");
  const [assocLinkId, setAssocLinkId] = useState("");
  const [assocSaving, setAssocSaving] = useState(false);

  // portal
  const [portalRes, setPortalRes] = useState(null); // resident for portal dialog

  // presentazione Sedio ai condòmini
  const [presentBuilding, setPresentBuilding] = useState(null);
  const [presentResend, setPresentResend] = useState(false);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const _bq = buildingSearch.trim().toLowerCase();
  const filteredBuildings = _bq
    ? buildings.filter((b) => [b.name, b.address, b.citta, b.codice_fiscale, b.provincia, b.cap]
        .some((v) => (v ?? "").toString().toLowerCase().includes(_bq)))
    : buildings;
  const _resQuery = resSearch.trim().toLowerCase();
  const baseResidents = _resQuery
    ? residents.filter((r) => [r.nome, r.codice_fiscale, r.email, r.telefono, r.scala, r.interno]
        .some((v) => (v ?? "").toString().toLowerCase().includes(_resQuery)))
    : residents;
  const displayed = sortKey === "custom" ? baseResidents : [...baseResidents].sort((a, b) => {
    if (sortKey === "unita") {
      const ua = `${a.scala || ""} ${a.interno || ""}`.trim().toLowerCase();
      const ub = `${b.scala || ""} ${b.interno || ""}`.trim().toLowerCase();
      const c = ua.localeCompare(ub, "it", { numeric: true });
      if (c !== 0) return sortDir === "asc" ? c : -c;
      const rc = roleRank(a) - roleRank(b);
      if (rc !== 0) return rc;
      return (a.nome || "").localeCompare(b.nome || "", "it");
    }
    let av, bv;
    if (sortKey === "scala") {
      av = `${a.scala || ""} ${a.interno || ""}`.trim().toLowerCase();
      bv = `${b.scala || ""} ${b.interno || ""}`.trim().toLowerCase();
    } else {
      av = (a.nome || "").toLowerCase();
      bv = (b.nome || "").toLowerCase();
    }
    const cmp = av.localeCompare(bv, "it", { numeric: true });
    return sortDir === "asc" ? cmp : -cmp;
  });

  // Sfumature alterne RAGGRUPPATE per INTERNO (stile tabelle Pages/Numbers): tutte le righe dello stesso
  // interno condividono la stessa tinta; interni consecutivi alternano due tinte (1→A, 2→B, 3→A, 4→B…).
  const shadeForResident = (() => {
    const map = {}; let gi = 0;
    displayed.forEach((r) => {
      const k = (r.interno || "").trim().toLowerCase() || "__none__";
      if (!(k in map)) { map[k] = gi++; }
    });
    const SHADES = ["bg-[#007BFF]/[0.07] dark:bg-[#4C8DFF]/[0.16]", "bg-slate-500/[0.07] dark:bg-slate-200/[0.09]"];
    return (r) => SHADES[map[(r.interno || "").trim().toLowerCase() || "__none__"] % 2];
  })();

  const residentsCacheRef = useRef({});
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected]);

  const loadResidents = useCallback(async (bid) => {
    const r = await api.get(`/buildings/${bid}/residents`);
    residentsCacheRef.current[bid] = r.data;
    // Applica solo se il condominio è ancora quello selezionato (evita race al cambio rapido).
    if (selectedIdRef.current === bid) setResidents(r.data);
    return r.data;
  }, []);

  const selectBuilding = useCallback(async (b) => {
    selectedIdRef.current = b.id;
    setSelected(b);
    setDiscrepancies([]);
    // Mostra ISTANTANEAMENTE l'anagrafica dalla cache (o vuota) e rivalida in background.
    setResidents(residentsCacheRef.current[b.id] || []);
    loadResidents(b.id);
  }, [loadResidents]);

  const load = useCallback(async () => {
    const r = await api.get("/buildings");
    setBuildings(r.data);
    if (!r.data.length) { setSelected(null); setResidents([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-anteprima del primo condominio solo su desktop e una sola volta: su mobile la vista
  // parte dalla lista (stile posta) e il tasto "Torna ai condomìni" deve restare valido.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || !buildings.length) return;
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches) {
      selectBuilding(buildings[0]);
      autoSelectedRef.current = true;
    }
  }, [buildings, selectBuilding]);

  // Prefetch in BACKGROUND delle anagrafiche di tutti i condomìni: scalda la cache così il
  // passaggio da un condominio all'altro è ISTANTANEO anche al primo accesso. Sequenziale per
  // non generare picchi di richieste con molti condomìni.
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current || !buildings.length) return;
    prefetchedRef.current = true;
    (async () => {
      for (const b of buildings) {
        if (residentsCacheRef.current[b.id]) continue;
        try { await loadResidents(b.id); } catch { /* ignora errori di prefetch */ }
      }
    })();
  }, [buildings, loadResidents]);


  // --- Tour guidato: crea un condominio DEMO (con residenti e documento) e lo elimina a fine tour ---
  const demoBidRef = useRef(null);
  const startTourDemo = useCallback(async () => {
    try {
      const r = await api.post("/tour-demo/condominio");
      demoBidRef.current = r.data.id;
      const bl = await api.get("/buildings");
      setBuildings(bl.data);
      const demo = bl.data.find((b) => b.id === r.data.id) || r.data;
      await selectBuilding(demo);
    } catch (e) { console.debug("tour demo condominio:", e); }
  }, [selectBuilding]);
  const endTourDemo = useCallback(async () => {
    try { await api.delete("/tour-demo/condominio"); } catch (e) { console.debug("tour demo delete:", e); }
    demoBidRef.current = null;
    try {
      const bl = await api.get("/buildings");
      setBuildings(bl.data);
      if (bl.data.length) selectBuilding(bl.data[0]); else { setSelected(null); setResidents([]); }
    } catch (e) { console.debug(e); }
  }, [selectBuilding]);

  const DANEA_ACCEPTED = [".xlsx", ".xls", ".pdf", ".png", ".jpg", ".jpeg", ".webp", ".txt", ".csv"];
  const onDaneaDrop = (e) => {
    e.preventDefault();
    setDaneaDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!DANEA_ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      return toast.error("Formato non supportato. Usa Excel, PDF, immagine o CSV/testo.");
    }
    setFile(f);
  };

  const openImportPreview = async (fileObj, buildingId) => {
    const fd = new FormData();
    fd.append("file", fileObj);
    if (buildingId) fd.append("building_id", buildingId);
    const r = await api.post("/import/anagrafica/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPreview({
      ...r.data,
      residents: (r.data.residents || []).map((x) => ({ ...EMPTY_RESIDENT, ...x })),
    });
  };

  const importDanea = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleziona un file da importare");
    setImporting(true);
    try { await openImportPreview(file, null); setImportOpen(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Estrazione anagrafica fallita"); }
    finally { setImporting(false); }
  };

  const persistImport = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const r = await api.post("/import/anagrafica/confirm", {
        building: preview.building, residents: preview.residents, building_id: preview.building_id || "",
      });
      const d = r.data;
      const parts = [];
      if (d.residents_imported) parts.push(`${d.residents_imported} nuovi`);
      if (d.residents_updated) parts.push(`${d.residents_updated} aggiornati`);
      const detail = parts.length ? parts.join(", ") : "nessuna modifica";
      toast.success(d.building_created
        ? `Condominio "${d.building.name}" creato — ${detail}`
        : `Condominio "${d.building.name}": ${detail}`);
      if (d.building_created && !d.building?.subscribed) {
        setTimeout(() => toast.warning("Il condominio importato non è collegato. Attivalo dalla sezione \"Acquisti e collegamento\" per usarlo con tutte le funzioni.", { duration: 8000 }), 500);
      }
      if (d.demo_purged) setTimeout(() => toast.info("Dati demo rimossi: ora vedi i tuoi dati reali."), 600);
      setDiscrepancies(d.discrepancies || []);
      if ((d.discrepancies || []).length) toast.info(`${d.discrepancies.length} condòmini con valori aggiornati dal file (sovrascritti).`);
      const wasBuilding = preview.building_id;
      setFile(null); setPreview(null);
      await load();
      if (wasBuilding && selected) await loadResidents(selected.id);
      else await selectBuilding(d.building);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fallito");
    } finally { setConfirming(false); }
  };

  const totalMillesimi = residents.reduce((s, r) => s + (parseFloat(String(r.millesimi).replace(",", ".")) || 0), 0);
  const missingCf = residents.filter((r) => !(r.codice_fiscale || "").trim()).length;
  const catastoSync = useCatastoSync(selected, residents, async (cat) => {
    setSelected((s) => (s ? { ...s, catasto: cat } : s));
    if (selected?.id) loadResidents(selected.id);
  });

  // Import AI dei condòmini in un condominio specifico
  const buildingImportRef = useRef(null);
  const [buildingImporting, setBuildingImporting] = useState(false);
  const [buildingImportOpen, setBuildingImportOpen] = useState(false);
  const [buildingFile, setBuildingFile] = useState(null);
  const [buildingDrag, setBuildingDrag] = useState(false);
  const importIntoBuilding = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    setBuildingImporting(true);
    try { await openImportPreview(f, selected.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Estrazione anagrafica fallita"); }
    finally { setBuildingImporting(false); if (buildingImportRef.current) buildingImportRef.current.value = ""; }
  };
  const onBuildingDrop = (e) => {
    e.preventDefault();
    setBuildingDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!DANEA_ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext))) {
      return toast.error("Formato non supportato. Usa Excel, PDF, immagine o CSV/testo.");
    }
    setBuildingFile(f);
  };
  const submitBuildingImport = async (e) => {
    e.preventDefault();
    if (!buildingFile || !selected) return toast.error("Seleziona un file da importare");
    setBuildingImporting(true);
    try { await openImportPreview(buildingFile, selected.id); setBuildingImportOpen(false); setBuildingFile(null); }
    catch (err) { toast.error(err.response?.data?.detail || "Estrazione anagrafica fallita"); }
    finally { setBuildingImporting(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/buildings/${toDelete.id}`);
      toast.success(`Condominio "${toDelete.name}" eliminato`);
      if (selected?.id === toDelete.id) { setSelected(null); setResidents([]); }
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione");
    } finally { setDeleting(false); }
  };

  const askDeleteBuilding = async (b) => {
    setDeletePortalCount(0);
    setToDelete(b);
    try {
      const { data } = await api.get(`/buildings/${b.id}/portal-count`);
      setDeletePortalCount(data?.count || 0);
    } catch { /* se il conteggio fallisce, mostro comunque il dialog senza avviso */ }
  };

  const saveResident = async () => {
    if (!resDialog?.data?.nome?.trim()) return toast.error("Il nominativo è obbligatorio");
    setResSaving(true);
    try {
      if (resDialog.mode === "add") {
        await api.post("/residents", { building_id: selected.id, ...resDialog.data });
        toast.success("Condòmino aggiunto");
      } else {
        await api.put(`/residents/${resDialog.data.id}`, resDialog.data);
        toast.success("Anagrafica aggiornata");
      }
      setResDialog(null);
      await loadResidents(selected.id);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setResSaving(false); }
  };

  const confirmDeleteResident = async () => {
    if (!resToDelete) return;
    const delId = resToDelete.id;
    try {
      await api.delete(`/residents/${delId}`);
      toast.success("Condòmino eliminato");
      setResToDelete(null);
      setResidents((rs) => rs.filter((x) => x.id !== delId));   // aggiornamento istantaneo
      await loadResidents(selected.id);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const confirmClearAll = async () => {
    if (!selected) return;
    setClearingAll(true);
    try {
      const { data } = await api.delete(`/buildings/${selected.id}/residents`);
      toast.success(`Anagrafica svuotata: ${data.residents_deleted} condòmini eliminati`);
      setClearAllOpen(false);
      // aggiornamento ISTANTANEO (ottimistico): svuota subito lista condòmini e immobili non associati
      setResidents([]);
      setSelected((s) => (s ? { ...s, catasto: { ...(s.catasto || {}), unassigned_units: [] } } : s));
      await loadResidents(selected.id);
      await load();
      try {
        const b = (await api.get("/buildings")).data.find((x) => x.id === selected.id);
        if (b) setSelected(b);
      } catch (err) { console.debug("refresh selected:", err); }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante lo svuotamento"); }
    finally { setClearingAll(false); }
  };

  // Suggerisce l'interno in base al SUBALTERNO: un comproprietario condivide il sub con qualcuno già in anagrafica
  const suggestForUnit = (unit) => {
    const key = fpsKey(unit);
    const out = [];
    (residents || []).forEach((r) => {
      (r.catasto_immobili || []).forEach((im) => {
        if (fpsKey(im) === key) out.push({ id: r.id, interno: r.interno || "", scala: r.scala || "", nome: r.nome });
      });
    });
    return out;
  };
  const openAssoc = (unit, t, key) => {
    const sug = suggestForUnit(unit);
    const linkId = sug[0]?.id || (residents[0]?.id || "");
    setAssoc({ unit, t, key });
    setAssocMode("add");
    setAssocLinkId(linkId);
    const lr = (residents || []).find((r) => r.id === linkId);
    setAssocInterno(sug[0]?.interno || lr?.interno || "");
    setAssocScala(sug[0]?.scala || lr?.scala || "");
    setAssocReplaceId(sug[0]?.id || (residents[0]?.id || ""));
  };
  const confirmAssoc = async () => {
    if (!assoc || !selected) return;
    setAssocSaving(true);
    try {
      const { t, unit, key } = assoc;
      // in modalità "aggiungi" l'interno/scala derivano dal condòmino selezionato a cui collegare
      let interno = assocInterno, scala = assocScala;
      if (assocMode === "add") {
        const lr = (residents || []).find((r) => r.id === assocLinkId);
        if (lr) { interno = lr.interno || ""; scala = lr.scala || ""; }
      }
      const { data } = await api.post("/residents/from-catasto", {
        building_id: selected.id,
        nome: t.nome,
        codice_fiscale: t.cf || "",
        interno: interno || "",
        scala: scala || "",
        mode: assocMode,
        replace_resident_id: assocMode === "replace" ? (assocReplaceId || null) : null,
        immobile: { ...unit, titolarita: t.quota ? `Proprieta' per ${t.quota}` : (unit.titolarita || "") },
      });
      setAddedUnassigned((s) => ({ ...s, [key]: true }));
      toast.success(data.replaced ? `Proprietario sostituito con ${t.nome}` : (data.created ? `${t.nome} aggiunto all'anagrafica` : `Immobile associato a ${t.nome}`));
      setAssoc(null);
      // aggiornamento ISTANTANEO: rimuovi SOLO l'intestatario associato dall'immobile.
      // L'immobile resta finché ci sono altri comproprietari da associare.
      const sameIntest = (x) => {
        const xcf = (x.cf || "").replace(/\s/g, "").toUpperCase();
        const tcf = (t.cf || "").replace(/\s/g, "").toUpperCase();
        if (xcf && tcf) return xcf === tcf;
        return (x.nome || "").trim().toLowerCase() === (t.nome || "").trim().toLowerCase();
      };
      setSelected((s) => {
        if (!s) return s;
        const ua = ((s.catasto || {}).unassigned_units || []).map((x) => {
          if (fpsKey(x) !== fpsKey(unit)) return x;
          const remaining = (x.intestatari || []).filter((it) => !sameIntest(it));
          return { ...x, intestatari: remaining };
        }).filter((x) => (x.intestatari || []).length > 0);
        return { ...s, catasto: { ...(s.catasto || {}), unassigned_units: ua } };
      });
      await loadResidents(selected.id);
      await load();
      try {
        const b = (await api.get("/buildings")).data.find((x) => x.id === selected.id);
        if (b) setSelected(b);
      } catch (err) { console.debug("refresh selected:", err); }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'operazione"); }
    finally { setAssocSaving(false); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= residents.length) return;
    const next = [...residents];
    [next[idx], next[j]] = [next[j], next[idx]];
    setResidents(next);
    try {
      await api.put(`/buildings/${selected.id}/residents/reorder`, { ordered_ids: next.map((r) => r.id) });
    } catch { toast.error("Errore riordino"); loadResidents(selected.id); }
  };

  return (
    <div className="fade-in flex flex-col h-[calc(100dvh-3.5rem)] md:h-[100dvh] overflow-hidden">
      {(importing || buildingImporting) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="ai-import-loading">
          <div className="bg-card border border-border rounded-2xl shadow-2xl px-8 py-7 flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative h-14 w-14">
              <span className="absolute inset-0 rounded-full border-4 border-[#007BFF]/20" />
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#007BFF] animate-spin" />
              <FileText className="absolute inset-0 m-auto h-6 w-6 text-[#007BFF] animate-pulse" />
            </div>
            <div className="text-center">
              <div className="font-head text-lg font-semibold flex items-center justify-center gap-1.5">
                L'AI sta leggendo l'anagrafica
                <span className="inline-flex gap-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#007BFF] animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">Sto estraendo condòmini, millesimi e contatti dal file. Ci vuole qualche secondo…</p>
            </div>
          </div>
        </div>
      )}
      <header className="px-4 sm:px-10 pt-4 sm:pt-5 pb-4 border-b border-border bg-card/50 shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-1.5">Amministrazione Condominiale</div>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tighter">Condomìni</h1>
            <p className="text-muted-foreground text-sm mt-1">Gestione multi-stabile con anagrafica condòmini, portali di accesso e area documentale. Importa da Excel, PDF (Danea e altri gestionali) o inserisci manualmente.</p>
            <PageTour storageKey="tour_condomini_v2" className="mt-2" steps={CONDOMINI_TOUR_STEPS} onOpen={startTourDemo} onClose={endTourDemo} />
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <NewBuildingButton onCreated={async (b) => { await load(); selectBuilding(b); }} />
            <Button data-testid="danea-import-btn" onClick={() => setImportOpen(true)} variant="outline"
              className="rounded-md h-11 border-[#007BFF]/40 text-[#007BFF] hover:bg-[#007BFF]/5">
              <Upload className="h-4 w-4 mr-1.5" />Importa con AI
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
        {buildings.length === 0 ? (
          <div className="w-full text-center py-16 text-muted-foreground flex flex-col items-center gap-3 px-6">
            <Building2 className="h-10 w-10 opacity-40" />Nessun condominio. Crea un condominio con "Nuovo condominio" oppure importa un file Excel/PDF con "Importa con AI".
          </div>
        ) : (
          <>
            {/* COLONNA SINISTRA: lista condomìni + ricerca (stile Posta) */}
            <aside data-testid="buildings-sidebar"
              className={`w-full md:w-[360px] md:min-w-[320px] border-r border-border bg-card/40 flex-col min-h-0 ${selected ? "hidden md:flex" : "flex"}`}>
              <div className="p-3 border-b border-border shrink-0 relative" data-testid="building-search">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="building-search-input"
                  type="text"
                  value={buildingSearch}
                  onChange={(e) => setBuildingSearch(e.target.value)}
                  placeholder="Cerca condominio…"
                  className="w-full h-10 pl-9 pr-9 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40 focus:border-[#007BFF]"
                />
                {buildingSearch && (
                  <button data-testid="building-search-clear" onClick={() => setBuildingSearch("")}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Pulisci ricerca">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto scroll-thin">
                {filteredBuildings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground text-sm px-4" data-testid="building-search-empty">
                    Nessun condominio corrisponde a «{buildingSearch}».
                  </div>
                ) : (
                  <BuildingsList
                    buildings={filteredBuildings}
                    selectedId={selected?.id}
                    onSelect={selectBuilding}
                    onDelete={(b) => askDeleteBuilding(b)}
                    onPresent={(b, resend) => { setPresentResend(resend); setPresentBuilding(b); }}
                  />
                )}
              </div>
            </aside>

            {/* COLONNA DESTRA: dettaglio del condominio selezionato (stile anteprima mail) */}
            <main data-testid="building-detail-pane"
              className={`flex-1 min-w-0 overflow-y-auto scroll-thin ${selected ? "flex" : "hidden md:flex"} flex-col`}>
              {!selected ? (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-10 text-center">
                  <Building2 className="h-12 w-12 opacity-30" />
                  <p className="text-sm">Seleziona un condominio dalla lista per vederne i dettagli.</p>
                </div>
              ) : (
                <div className="p-4 sm:p-8 space-y-8">
                  <button onClick={() => setSelected(null)} data-testid="building-detail-back"
                    className="md:hidden inline-flex items-center gap-1.5 text-sm text-[#007BFF] font-medium">
                    <ChevronLeft className="h-4 w-4" />Torna ai condomìni
                  </button>

                {selected && (
                  <div className="grid grid-cols-1 gap-4 items-stretch">
                    <BuildingNotices building={selected} residents={residents} />
                    <BuildingPolls building={selected} residents={residents} />
                  </div>
                )}

            {selected && <BuildingDocuments building={selected} residents={residents} />}

            {selected && (
              <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="anagrafica-section">
                <AnagraficaHeader
                  selected={selected}
                  buildingImporting={buildingImporting}
                  buildingImportRef={buildingImportRef}
                  importIntoBuilding={importIntoBuilding}
                  onOpenImport={() => { setBuildingFile(null); setBuildingImportOpen(true); }}
                  catastoSync={catastoSync}
                  totalMillesimi={totalMillesimi}
                  residentsCount={residents.length}
                  missingCf={missingCf}
                  sortKey={sortKey}
                  setSortKey={setSortKey}
                  setSortDir={setSortDir}
                  onEditSaved={async () => { await load(); const b = (await api.get("/buildings")).data.find((x) => x.id === selected.id); if (b) setSelected(b); }}
                  onAddResident={() => setResDialog({ mode: "add", data: { ...EMPTY_RESIDENT, catasto_immobili: [{ foglio: selected.catasto?.foglio || "", particella: selected.catasto?.particella || "", subalterno: "" }] } })}
                  onClearAnagrafica={() => setClearAllOpen(true)}
                />
                {selected.subscription_status === "attivo" && (
                  <div className="border-b border-border" data-testid="catasto-info-strip">
                    {catastoSync.busy && catastoSync.syncMsg && (
                      <div className="px-6 py-2 bg-[#007BFF]/5" data-testid="catasto-sync-progress">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs text-[#007BFF] flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />{catastoSync.syncMsg}
                          </div>
                          <button data-testid="catasto-sync-stop-btn" onClick={() => catastoSync.cancelSync()}
                            title="Interrompi la ricerca degli immobili catastali (utile se sono troppi)"
                            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors shrink-0">
                            <StopCircle className="h-3.5 w-3.5" />Interrompi
                          </button>
                        </div>
                        {catastoSync.syncProgress && catastoSync.syncProgress.total > 0 && (
                          <div className="mt-1.5 h-1.5 w-full max-w-md rounded-full bg-[#007BFF]/15 overflow-hidden"
                            data-testid="catasto-sync-bar">
                            <div className="h-full rounded-full bg-[#007BFF] transition-all duration-500"
                              style={{ width: `${Math.min(100, Math.round((catastoSync.syncProgress.done / catastoSync.syncProgress.total) * 100))}%` }} />
                          </div>
                        )}
                      </div>
                    )}
                    {catastoSync.addrPreview && (
                      catastoSync.addrPreview.via && catastoSync.addrPreview.civico ? (
                        <div className="px-6 py-2 text-xs flex items-start gap-2 bg-[#007BFF]/5" data-testid="catasto-address-preview">
                          <MapPin className="h-3.5 w-3.5 text-[#007BFF] shrink-0 mt-0.5" />
                          <span>La ricerca catastale userà → Via: <strong>{catastoSync.addrPreview.via}</strong> · Civico: <strong>{catastoSync.addrPreview.civico}{catastoSync.addrPreview.esponente ? `/${catastoSync.addrPreview.esponente}` : ""}</strong>{catastoSync.addrPreview.comune ? <> · Comune: <strong>{catastoSync.addrPreview.comune}</strong>{catastoSync.addrPreview.provincia ? ` (${catastoSync.addrPreview.provincia})` : ""}</> : null}</span>
                        </div>
                      ) : (
                        <div className="px-6 py-2 text-xs flex items-start gap-2 bg-amber-500/10 text-amber-700 dark:text-amber-500" data-testid="catasto-address-warning">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span>{!catastoSync.addrPreview.via ? "Indirizzo del condominio mancante o non valido: " : "Numero civico non riconosciuto nell'indirizzo: "}completa l'indirizzo (via + civico) con il pulsante di modifica del condominio per una ricerca catastale precisa.</span>
                        </div>
                      )
                    )}
                    {catastoSync.addrPreview && catastoSync.addrPreview.suggest_normalization && catastoSync.addrPreview.via_google && (
                      <div className="px-6 py-2.5 text-xs flex items-start gap-2 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300" data-testid="catasto-address-suggestion">
                        <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                        <div className="flex-1">
                          <div>
                            {catastoSync.addrPreview.suggest_civico && !catastoSync.addrPreview.suggest_via
                              ? <>In anagrafica manca il numero civico. Su Google Maps risulta il civico <strong>{catastoSync.addrPreview.civico_google}</strong> per <strong>Via {catastoSync.addrPreview.via_google}</strong>. Senza civico la ricerca catastale può non trovare nulla.</>
                              : <>Su Google Maps l'indirizzo completo risulta <strong>Via {catastoSync.addrPreview.via_google}{catastoSync.addrPreview.civico_google ? `, ${catastoSync.addrPreview.civico_google}` : ""}</strong>. Il Catasto potrebbe non trovare nulla con un nome via abbreviato o un civico mancante.</>}
                          </div>
                          {catastoSync.viaOverride || catastoSync.civicoOverride ? (
                            <div className="mt-1 font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />La ricerca userà: Via {catastoSync.viaOverride || catastoSync.addrPreview.via}{(catastoSync.civicoOverride || catastoSync.addrPreview.civico) ? `, ${catastoSync.civicoOverride || catastoSync.addrPreview.civico}` : ""}</span>
                              <button data-testid="catasto-suggestion-undo" className="underline font-normal" onClick={() => { catastoSync.setViaOverride(""); catastoSync.setCivicoOverride(""); }}>annulla</button>
                            </div>
                          ) : (
                            <button data-testid="catasto-suggestion-apply"
                              onClick={() => { catastoSync.setViaOverride(catastoSync.addrPreview.via_google); catastoSync.setCivicoOverride(catastoSync.addrPreview.civico_google || ""); }}
                              className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-300 hover:underline">
                              Usa questo indirizzo per la ricerca →
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {catastoSync.cfCount > 0 ? (
                      <div className="px-6 py-2 text-xs text-muted-foreground bg-secondary/40" data-testid="catasto-cf-info">
                        La sincronizzazione interrogherà <strong>{catastoSync.cfCount}</strong> condòmini con codice fiscale · costo fisso <strong>3 SedioCoin</strong> a prescindere dal numero di CF.
                      </div>
                    ) : null}
                  </div>
                )}
                {selected.subscription_status !== "attivo" && (
                  <a href="/acquisti" data-testid="building-gated-notice"
                    className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 hover:bg-amber-100 transition-colors cursor-pointer">
                    <Lock className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                    <span className="text-[13px] text-amber-800">
                      <strong>Condominio non collegato.</strong> Non puoi aggiungere condòmini, usare l'area documenti o le altre funzioni finché non lo attivi.{" "}
                      <span className="font-semibold underline">Vai ad "Acquisti e collegamento" per collegarlo →</span>
                    </span>
                  </a>
                )}
                {discrepancies.length > 0 && (
                  <div data-testid="import-discrepancies" className="mx-6 mt-4 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                        <AlertTriangle className="h-4 w-4" />
                        {discrepancies.length} condòmini aggiornati dal file
                      </div>
                      <button data-testid="dismiss-discrepancies" onClick={() => setDiscrepancies([])} className="text-xs text-amber-700 hover:underline">Chiudi</button>
                    </div>
                    <p className="text-[11px] text-amber-700 mb-2">Il file importato conteneva valori diversi da quelli presenti: <strong>sono stati sovrascritti con quelli del file.</strong></p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {discrepancies.map((d, i) => (
                        <div key={i} className="text-[11px] bg-white/60 rounded px-2 py-1.5 border border-amber-200">
                          <span className="font-semibold">{d.nome}</span>
                          {d.differenze.map((df, j) => (
                            <span key={j} className="block text-amber-800">
                              {df.campo}: attuale <span className="font-mono">"{df.attuale}"</span> → nel file <span className="font-mono">"{df.importato}"</span>
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <ResidentsTable
                  residents={residents}
                  displayed={displayed}
                  resSearch={resSearch}
                  setResSearch={setResSearch}
                  resQuery={_resQuery}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  toggleSort={toggleSort}
                  setSortKey={setSortKey}
                  move={move}
                  shadeForResident={shadeForResident}
                  selected={selected}
                  onPortal={setPortalRes}
                  onEdit={(r) => setResDialog({ mode: "edit", data: { ...r } })}
                  onDelete={setResToDelete}
                />
              </div>
            )}
            {selected && (selected.catasto?.unassigned_units || []).length > 0 && (
              <div className="bg-card border border-amber-300 rounded-md overflow-hidden mt-4" data-testid="anagrafica-unassigned-section">
                <div className="px-6 py-3 border-b border-amber-200 bg-amber-50">
                  <h3 className="font-head text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Immobili del catasto non ancora associati ({selected.catasto.unassigned_units.length})
                  </h3>
                  <p className="text-xs text-amber-700 mt-0.5">Immobili trovati al civico ma non collegati a nessun condòmino. Puoi associarli quando vuoi con "Aggiungi".</p>
                </div>
                <div className="divide-y divide-border">
                  {selected.catasto.unassigned_units.map((u, ui) => (
                    <div key={ui} className="px-6 py-3 text-xs" data-testid={`anagrafica-unassigned-${ui}`}>
                      <div className="font-medium text-foreground">
                        Fg. {u.foglio || "—"} · Part. {u.particella || "—"} · Sub. {u.subalterno || "—"}{u.categoria ? ` · ${u.categoria}` : ""}
                      </div>
                      {(() => { const s = suggestForUnit(u); return s.length > 0 ? (
                        <div className="text-[11px] text-sky-700 mt-0.5" data-testid={`unassigned-suggest-${ui}`}>
                          → Interno suggerito: <b>{[...new Set(s.map((x) => x.interno).filter(Boolean))].join(", ") || "—"}</b>
                          <span className="text-muted-foreground"> (stesso subalterno di {[...new Set(s.map((x) => x.nome))].join(", ")})</span>
                        </div>
                      ) : null; })()}
                      {(u.intestatari || []).length > 0 ? (
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                          {u.intestatari.map((t, ti) => {
                            const key = `persist-${ui}-${ti}`;
                            const added = !!addedUnassigned[key];
                            return (
                              <div key={ti} className="flex items-center gap-1">
                                <span className="text-muted-foreground">
                                  <b className="text-foreground">{t.nome || "—"}</b>
                                  {t.cf ? <span className="font-mono"> ({t.cf})</span> : ""}
                                  {t.quota ? ` · ${t.quota}` : ""}
                                </span>
                                {t.nome && (added
                                  ? <span className="text-emerald-600 text-[10px] font-medium whitespace-nowrap">✓ Aggiunto</span>
                                  : <Button size="sm" variant="outline" data-testid={`anagrafica-add-unassigned-${ui}-${ti}`}
                                      className="h-6 px-2 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                                      onClick={() => openAssoc(u, t, key)}>
                                      <Plus className="h-3 w-3 mr-0.5" />Aggiungi
                                    </Button>)}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-0.5 text-muted-foreground">Intestatario non disponibile</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              )}
            </main>
          </>
        )}
      </div>

      <AlertDialog open={catastoSync.noCfWarn} onOpenChange={catastoSync.setNoCfWarn}>
        <AlertDialogContent data-testid="catasto-no-cf-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />Codice fiscale mancante
            </AlertDialogTitle>
            <AlertDialogDescription>
              Nessun condòmino ha il <b>codice fiscale</b> in anagrafica. Normalmente la ricerca parte dai codici
              fiscali, ma posso comunque cercare <b>per indirizzo</b>: recupero i proprietari degli immobili al
              civico del condominio, li abbino ai nomi in anagrafica e, quando possibile, <b>calcolo io il codice
              fiscale</b>.
              <br /><br />
              <span className="text-amber-700">Nota:</span> questa modalità interroga <b>tutti</b> gli
              immobili del civico. Il costo è comunque <b>fisso: 3 SedioCoin</b> per sincronizzazione. In alternativa,
              inserisci i codici fiscali in anagrafica per una ricerca più mirata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catasto-no-cf-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="catasto-no-cf-address" className="bg-[#007BFF] hover:bg-[#0056B3]"
              onClick={() => { catastoSync.setNoCfWarn(false); catastoSync.sync(true); }}>
              Cerca per indirizzo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={catastoSync.confirmSyncOpen} onOpenChange={catastoSync.setConfirmSyncOpen}>
        <AlertDialogContent data-testid="catasto-cost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-[#007BFF]" />Costo della ricerca catastale
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {catastoSync.syncEstimate?.sufficient === false ? (
                  <>
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700" data-testid="catasto-cost-insufficient">
                      <b>SedioCoin insufficienti.</b> Per questa sincronizzazione servono
                      <b> {Number(catastoSync.syncEstimate?.charge_coin || 0)} SedioCoin</b> ma nel portafoglio hai
                      <b> {Number(catastoSync.syncEstimate?.coin_balance || 0)} SedioCoin</b>.
                    </div>
                    <div>Matura altri SedioCoin completando l'onboarding dei condomìni (piani Premium/Elite), poi riprova.</div>
                  </>
                ) : (
                  <>
                    <div>
                      Questa sincronizzazione costerà <b className="text-[#007BFF]" data-testid="catasto-cost-amount">{Number(catastoSync.syncEstimate?.charge_coin || 0)} SedioCoin</b> (tariffa fissa),
                      che verranno <b>scalati dal tuo portafoglio Sedio Partner</b>.
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2">
                      <span className="text-muted-foreground">SedioCoin disponibili</span>
                      <b data-testid="catasto-cost-balance">{Number(catastoSync.syncEstimate?.coin_balance || 0)} SC</b>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2">
                      <span className="text-muted-foreground">Saldo dopo la ricerca</span>
                      <b>{(Number(catastoSync.syncEstimate?.coin_balance || 0) - Number(catastoSync.syncEstimate?.charge_coin || 0))} SC</b>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Codici fiscali interrogati: {catastoSync.syncEstimate?.cf_total || 0}
                      {catastoSync.syncEstimate?.cached_searches ? ` · ${catastoSync.syncEstimate.cached_searches} già in cache` : ""}.
                      Il costo è <b>fisso (3 SedioCoin)</b> a prescindere dal numero di condòmini.
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catasto-cost-cancel">Annulla</AlertDialogCancel>
            {catastoSync.syncEstimate?.sufficient !== false && (
              <AlertDialogAction data-testid="catasto-cost-confirm" className="bg-[#007BFF] hover:bg-[#0056B3]"
                onClick={() => catastoSync.confirmSyncNow()}>
                Conferma e cerca
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <CatastoProposalDialog catastoSync={catastoSync} addedUnassigned={addedUnassigned} openAssoc={openAssoc} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-building-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il condominio?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-semibold text-foreground">{toDelete?.name}</span> e tutta la sua anagrafica condòmini. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deletePortalCount > 0 && (
            <div data-testid="delete-building-portal-warning"
              className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
              <span>
                Attenzione: {deletePortalCount === 1 ? "c'è" : "ci sono"} <b>{deletePortalCount} portale{deletePortalCount === 1 ? "" : "i"} condòmino attiv{deletePortalCount === 1 ? "o" : "i"}</b> collegat{deletePortalCount === 1 ? "o" : "i"} a questo condominio. Eliminando il condominio {deletePortalCount === 1 ? "quel portale non avrà più" : "quei portali non avranno più"} accesso ai dati.
              </span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-building">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-building" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Eliminazione…</> : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resToDelete} onOpenChange={(o) => !o && setResToDelete(null)}>
        <AlertDialogContent data-testid="delete-resident-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il condòmino?</AlertDialogTitle>
            <AlertDialogDescription>Stai per rimuovere <b>{resToDelete?.nome}</b> dall'anagrafica.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-resident" onClick={(e) => { e.preventDefault(); confirmDeleteResident(); }} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearAllOpen} onOpenChange={(o) => { if (!clearingAll) setClearAllOpen(o); }}>
        <AlertDialogContent data-testid="clear-anagrafica-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-600" />Cancellare tutta l'anagrafica?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>tutti i {residents.length} condòmini</b> di <b>{selected?.name}</b>.
              {(selected?.catasto?.unassigned_units || []).length > 0 && (
                <> Verrà svuotata <b>anche la lista dei {selected.catasto.unassigned_units.length} immobili non associati</b>.</>
              )}
              {" "}L'operazione non si può annullare. Il condominio e i portali non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-anagrafica-cancel" disabled={clearingAll}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="clear-anagrafica-confirm" disabled={clearingAll}
              onClick={(e) => { e.preventDefault(); confirmClearAll(); }} className="bg-red-600 hover:bg-red-700">
              {clearingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Cancella tutto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!assoc} onOpenChange={(o) => { if (!assocSaving && !o) setAssoc(null); }}>
        <DialogContent className="max-w-md" data-testid="assoc-dialog">
          <DialogHeader>
            <DialogTitle>Associa immobile catastale</DialogTitle>
            <DialogDescription>
              Nuovo intestatario: <b className="text-foreground">{assoc?.t?.nome}</b>
              {assoc?.t?.cf ? <span className="font-mono"> ({assoc.t.cf})</span> : ""}
              {assoc?.unit ? <><br />Immobile: Fg. {assoc.unit.foglio || "—"} · Part. {assoc.unit.particella || "—"} · Sub. {assoc.unit.subalterno || "—"}{assoc.unit.categoria ? ` · ${assoc.unit.categoria}` : ""}</> : null}
            </DialogDescription>
          </DialogHeader>
          {assoc && (() => {
            const sug = suggestForUnit(assoc.unit);
            return (
              <div className="space-y-4">
                {sug.length > 0 && (
                  <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="assoc-suggestion">
                    Suggerimento: stesso subalterno di <b>{[...new Set(sug.map((x) => x.nome))].join(", ")}</b> (interno <b>{[...new Set(sug.map((x) => x.interno).filter(Boolean))].join(", ") || "—"}</b>). Probabile comproprietà o subentro.
                  </div>
                )}
                <div className="flex gap-2">
                  <button type="button" data-testid="assoc-mode-add"
                    onClick={() => setAssocMode("add")}
                    className={`flex-1 text-xs px-3 py-2 rounded-md border ${assocMode === "add" ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-medium" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    Aggiungi come comproprietario
                  </button>
                  <button type="button" data-testid="assoc-mode-replace"
                    onClick={() => setAssocMode("replace")}
                    className={`flex-1 text-xs px-3 py-2 rounded-md border ${assocMode === "replace" ? "border-amber-400 bg-amber-50 text-amber-700 font-medium" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    Sostituisci vecchio proprietario
                  </button>
                </div>
                {assocMode === "add" ? (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Collega al condòmino</label>
                    <select value={assocLinkId}
                      onChange={(e) => {
                        setAssocLinkId(e.target.value);
                        const lr = (residents || []).find((r) => r.id === e.target.value);
                        setAssocInterno(lr?.interno || "");
                        setAssocScala(lr?.scala || "");
                      }}
                      data-testid="assoc-link-select"
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                      <option value="">— seleziona condòmino —</option>
                      {(residents || []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nome}{r.interno ? ` · int. ${r.interno}` : ""}{r.scala ? ` · scala ${r.scala}` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-emerald-700 mt-1">
                      {(residents || []).length === 0
                        ? "Nessun condòmino in anagrafica: verrà creato un nuovo condòmino."
                        : <>{assoc.t?.nome} verrà aggiunto come comproprietario allo stesso interno{assocInterno ? <> (<b>{assocInterno}</b>)</> : ""} del condòmino selezionato.</>}
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Condòmino da sostituire</label>
                    <select value={assocReplaceId} onChange={(e) => setAssocReplaceId(e.target.value)}
                      data-testid="assoc-replace-select"
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                      <option value="">— seleziona —</option>
                      {(residents || []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.nome}{r.interno ? ` · int. ${r.interno}` : ""}{r.codice_fiscale ? ` · ${r.codice_fiscale}` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-amber-600 mt-1">Il vecchio proprietario verrà sostituito con {assoc.t?.nome} (nome, CF e immobile aggiornati; contatti azzerati).</p>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssoc(null)} disabled={assocSaving} data-testid="assoc-cancel">Annulla</Button>
            <Button onClick={confirmAssoc} disabled={assocSaving || (assocMode === "replace" && !assocReplaceId)}
              className="bg-[#007BFF] hover:bg-[#0056B3]" data-testid="assoc-confirm">
              {assocSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              {assocMode === "replace" ? "Sostituisci" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResidentDialog state={resDialog} onChange={(d) => setResDialog((s) => ({ ...s, data: d }))} onClose={() => setResDialog(null)} onSave={saveResident} saving={resSaving} />

      <PortalForResidentDialog resident={portalRes} buildingName={selected?.name} onClose={() => setPortalRes(null)} onDone={() => loadResidents(selected.id)} />

      <PresentationDialog
        building={presentBuilding}
        open={!!presentBuilding}
        onClose={() => setPresentBuilding(null)}
        onSent={(info) => setBuildings((prev) => prev.map((x) => x.id === presentBuilding?.id ? { ...x, ...info } : x))}
      />

      <Dialog open={importOpen} onOpenChange={(o) => { if (!importing) { setImportOpen(o); if (!o) setFile(null); } }}>
        <DialogContent className="max-w-lg" data-testid="danea-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#007BFF]" />Importa anagrafica con l'AI</DialogTitle>
            <DialogDescription>
              Carica un file da <strong>qualsiasi gestionale o Excel</strong>: l'AI riconosce le colonne ed estrae automaticamente condominio e condòmini (nome, interno, millesimi, C.F., contatti, proprietario/inquilino).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={importDanea}>
            <label
              onDragOver={(e) => { e.preventDefault(); setDaneaDrag(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDaneaDrag(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDaneaDrag(false); }}
              onDrop={onDaneaDrop}
              data-testid="danea-dropzone"
              className={`block border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors duration-200 mb-4 ${daneaDrag ? "border-[#007BFF] bg-[#007BFF]/10" : "border-border hover:border-[#007BFF]"}`}>
              <input data-testid="danea-file-input" type="file" className="hidden" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={(e) => setFile(e.target.files[0] || null)} />
              <Upload className={`h-6 w-6 mx-auto mb-2 transition-colors ${daneaDrag ? "text-[#007BFF]" : "text-muted-foreground"}`} />
              <span className="text-sm text-muted-foreground">{file ? file.name : (daneaDrag ? "Rilascia qui il file" : "Trascina qui il file (Excel, PDF, Danea, CSV, immagine) o clicca")}</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={importing} onClick={() => { setImportOpen(false); setFile(null); }} data-testid="danea-import-cancel">Annulla</Button>
              <Button data-testid="danea-import-button" type="submit" disabled={importing || !file} className="bg-[#007BFF] hover:bg-[#0069D9]">
                {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Estrazione anagrafica…</> : "Analizza e verifica"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={buildingImportOpen} onOpenChange={(o) => { if (!buildingImporting) { setBuildingImportOpen(o); if (!o) setBuildingFile(null); } }}>
        <DialogContent className="max-w-lg" data-testid="building-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#007BFF]" />Importa condòmini con l'AI{selected ? ` — ${selected.name}` : ""}</DialogTitle>
            <DialogDescription>
              Carica un file da <strong>qualsiasi gestionale o Excel</strong>: l'AI riconosce le colonne ed estrae automaticamente i condòmini (nome, interno, millesimi, C.F., contatti, proprietario/inquilino) e li aggiunge a questo condominio.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBuildingImport}>
            <label
              onDragOver={(e) => { e.preventDefault(); setBuildingDrag(true); }}
              onDragEnter={(e) => { e.preventDefault(); setBuildingDrag(true); }}
              onDragLeave={(e) => { e.preventDefault(); setBuildingDrag(false); }}
              onDrop={onBuildingDrop}
              data-testid="building-dropzone"
              className={`block border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors duration-200 mb-4 ${buildingDrag ? "border-[#007BFF] bg-[#007BFF]/10" : "border-border hover:border-[#007BFF]"}`}>
              <input data-testid="building-file-input" type="file" className="hidden" accept=".xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.txt,.csv" onChange={(e) => setBuildingFile(e.target.files[0] || null)} />
              <Upload className={`h-6 w-6 mx-auto mb-2 transition-colors ${buildingDrag ? "text-[#007BFF]" : "text-muted-foreground"}`} />
              <span className="text-sm text-muted-foreground">{buildingFile ? buildingFile.name : (buildingDrag ? "Rilascia qui il file" : "Trascina qui il file (Excel, PDF, Danea, CSV, immagine) o clicca")}</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={buildingImporting} onClick={() => { setBuildingImportOpen(false); setBuildingFile(null); }} data-testid="building-import-cancel">Annulla</Button>
              <Button data-testid="building-import-submit" type="submit" disabled={buildingImporting || !buildingFile} className="bg-[#007BFF] hover:bg-[#0069D9]">
                {buildingImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Estrazione anagrafica…</> : "Analizza e verifica"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      <ImportPreviewDialog
        preview={preview}
        setPreview={setPreview}
        confirming={confirming}
        onConfirm={persistImport}
      />

    </div>
  );
}

